import {
  Camera,
  FileText,
  Loader2,
  Mic,
  Shield,
  Type,
  Upload,
} from "lucide-react";
import { useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { CameraCaptureMulti } from "@/components/smartfile/CameraCaptureMulti";
import { AudioRecorder } from "@/components/smartfile/AudioRecorder";
import { TextInput } from "@/components/smartfile/TextInput";
import { OnboardingTutorialModal } from "@/components/onboarding/OnboardingTutorialModal";
import { useOnboardingUpload } from "@/hooks/useOnboardingUpload";

interface Step3DocumentUploadProps {
  sessionId: string | null;
  patientId: string | null;
  onSessionCreated: (sessionId: string) => void;
  onPatientLinked: (patient: { id: string; name: string; cpf?: string }) => void;
  onBack: () => void;
  /** Conclui o onboarding e envia para o app sem exigir arquivo neste passo */
  onSkipDocument?: () => void | Promise<void>;
  skipLoading?: boolean;
  healthAreaName?: string;
}

export function Step3DocumentUpload({
  sessionId,
  patientId,
  onSessionCreated,
  onPatientLinked,
  onBack,
  onSkipDocument,
  skipLoading = false,
  healthAreaName,
}: Step3DocumentUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    uploading,
    uploadProgress,
    uploadStage,
    processFile,
    processText,
  } = useOnboardingUpload({
    onSessionCreated: (id, session) => {
      onSessionCreated(id);
    },
    onPatientLinked,
  });

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await processFile(file);
  };

  const handlePhotoCapture = async (files: File[]) => {
    if (files.length === 0) return;
    await processFile(files[0]);
  };

  const handleAudioCapture = async (file: File) => {
    await processFile(file);
  };

  const handleTextInput = async (text: string) => {
    await processText(text);
  };

  const [showCamera, setShowCamera] = useState(false);
  const [showRecorder, setShowRecorder] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  return (
    <>
      {showCamera && (
        <CameraCaptureMulti
          onCapture={handlePhotoCapture}
          onCancel={() => setShowCamera(false)}
          patientId={patientId}
        />
      )}
      {showRecorder && (
        <AudioRecorder
          onRecordingComplete={handleAudioCapture}
          onCancel={() => setShowRecorder(false)}
        />
      )}
      {showTextInput && (
        <TextInput
          onSubmit={handleTextInput}
          onCancel={() => setShowTextInput(false)}
          disabled={uploading}
        />
      )}

      {!showCamera && !showRecorder && !showTextInput && (
        <section className="bg-white border-2 rounded-2xl p-8 shadow-2xl space-y-6 animate-fade-in max-w-2xl mx-auto">
          <div className="text-center space-y-2">
            <div className="w-16 h-16 mx-auto bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl flex items-center justify-center mb-4">
              <Upload className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Dados do paciente</h2>
            <p className="text-muted-foreground">
              Envie um documento agora ou entre no sistema e faça isso depois
            </p>
          </div>

          {uploading && (
            <div className="space-y-3 bg-primary/5 rounded-lg p-4 border-2 border-primary/20">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{uploadStage}</span>
                <span className="text-sm text-muted-foreground">
                  {uploadProgress}%
                </span>
              </div>
              <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className={cn(
                "min-h-[140px] bg-background border border-border rounded-xl p-6",
                "flex flex-col items-center justify-center gap-3",
                "hover:border-transparent hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)]",
                "hover:bg-gradient-to-br hover:from-primary/5 hover:to-secondary/5",
                "transition-all duration-300 hover:scale-[1.02]",
                uploading && "opacity-50 pointer-events-none"
              )}
              onClick={() => setShowCamera(true)}
              disabled={uploading}
            >
              <div className="p-3 rounded-full bg-primary/10">
                <Camera className="h-8 w-8 text-primary" />
              </div>
              <span className="text-sm font-semibold">Câmera</span>
              <span className="text-xs text-muted-foreground text-center">
                Fotografe prontuários, anotações manuais
              </span>
            </button>

            <button
              type="button"
              className={cn(
                "min-h-[140px] bg-background border border-border rounded-xl p-6",
                "flex flex-col items-center justify-center gap-3",
                "hover:border-transparent hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)]",
                "hover:bg-gradient-to-br hover:from-primary/5 hover:to-secondary/5",
                "transition-all duration-300 hover:scale-[1.02]",
                uploading && "opacity-50 pointer-events-none"
              )}
              onClick={() => setShowRecorder(true)}
              disabled={uploading}
            >
              <div className="p-3 rounded-full bg-primary/10">
                <Mic className="h-8 w-8 text-primary" />
              </div>
              <span className="text-sm font-semibold">Áudio</span>
              <span className="text-xs text-muted-foreground text-center">
                Dite observações clínicas
              </span>
            </button>

            <button
              type="button"
              className={cn(
                "min-h-[140px] bg-background border border-border rounded-xl p-6",
                "flex flex-col items-center justify-center gap-3",
                "hover:border-transparent hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)]",
                "hover:bg-gradient-to-br hover:from-primary/5 hover:to-secondary/5",
                "transition-all duration-300 hover:scale-[1.02]",
                uploading && "opacity-50 pointer-events-none"
              )}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <div className="p-3 rounded-full bg-primary/10">
                <FileText className="h-8 w-8 text-primary" />
              </div>
              <span className="text-sm font-semibold">Arquivo</span>
              <span className="text-xs text-muted-foreground text-center">
                Suba PDFs, planilhas, resultados de exames
              </span>
            </button>

            <button
              type="button"
              className={cn(
                "min-h-[140px] bg-background border border-border rounded-xl p-6",
                "flex flex-col items-center justify-center gap-3",
                "hover:border-transparent hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)]",
                "hover:bg-gradient-to-br hover:from-primary/5 hover:to-secondary/5",
                "transition-all duration-300 hover:scale-[1.02]",
                uploading && "opacity-50 pointer-events-none"
              )}
              onClick={() => setShowTextInput(true)}
              disabled={uploading}
            >
              <div className="p-3 rounded-full bg-primary/10">
                <Type className="h-8 w-8 text-primary" />
              </div>
              <span className="text-sm font-semibold">Texto</span>
              <span className="text-xs text-muted-foreground text-center">
                Anote atendimentos, evoluções, métricas de saúde
              </span>
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.mp3,.wav,.m4a"
            onChange={handleFileSelected}
            className="hidden"
          />

          {onSkipDocument && (
            <div className="flex flex-col items-center gap-2 pt-2 border-t border-border/60">
              <Button
                type="button"
                variant="outline"
                className="w-full max-w-md"
                disabled={uploading || skipLoading}
                onClick={() => void onSkipDocument()}
              >
                {skipLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Abrindo o sistema…
                  </>
                ) : (
                  "Entrar sem enviar documento agora"
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center max-w-md">
                Você poderá cadastrar pacientes e enviar arquivos a qualquer
                momento dentro do app.
              </p>
            </div>
          )}

          <div className="flex items-center justify-center pt-4">
            <button
              type="button"
              className="text-sm text-primary hover:underline transition-colors"
              onClick={() => setShowTutorial(true)}
              disabled={uploading}
            >
              Ainda está com <span className="font-bold">dúvida</span>?{" "}
              <span className="font-bold">Clique aqui</span> e veja como é
              fácil!
            </button>
          </div>
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-4">
            <Shield className="h-4 w-4" />
            <span>Seus dados são protegidos conforme a LGPD</span>
          </div>
        </section>
      )}

      <OnboardingTutorialModal
        open={showTutorial}
        onOpenChange={setShowTutorial}
        healthAreaName={healthAreaName}
      />
    </>
  );
}

